import { type ReactNode } from "react";
import { Grid3X3, Sparkles, Wand2 } from "lucide-react";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { HoverInfoIcon } from "../reusable/HoverInfoIcon";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { useSymbolGenerator } from "./SymbolGeneratorContext";

export function SymbolGeneratorControls() {
    const {
        width,
        height,
        rows,
        cols,
        density,
        complexity,
        branching,
        mergeBias,
        directionalBiasMode,
        directionalBiasStrength,
        maxPathLength,
        pathGapCells,
        seed,
        symmetry,
        styleProfile,
        grammarMode,
        dotDensity,
        shapeFrequency,
        shapeType,
        pathMode,
        gridDisplacementStrength,
        cornerStyle,
        cornerAmount,
        curvature,
        strokeWidth,
        strokeColor,
        backgroundColor,
        exportBackground,
        showGridOverlay,
        liveGeneration,
        warning,
        setWidth,
        setHeight,
        setRows,
        setCols,
        setDensity,
        setComplexity,
        setBranching,
        setMergeBias,
        setDirectionalBiasMode,
        setDirectionalBiasStrength,
        setMaxPathLength,
        setPathGapCells,
        setSeed,
        setSymmetry,
        setStyleProfile,
        setGrammarMode,
        setDotDensity,
        setShapeFrequency,
        setShapeType,
        setPathMode,
        setGridDisplacementStrength,
        setCornerStyle,
        setCornerAmount,
        setCurvature,
        setStrokeWidth,
        setStrokeColor,
        setBackgroundColor,
        setExportBackground,
        setShowGridOverlay,
        setLiveGeneration,
        generate,
        reseed,
        randomizeParameters,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useSymbolGenerator();

    const numberHandler =
        (setter: (v: number) => void, min: number, max: number) =>
        (value: string | number) => {
            const n = typeof value === "number" ? value : Number(value);
            if (!Number.isFinite(n)) return;
            setter(Math.min(max, Math.max(min, n)));
        };

    return (
        <div className="panel-controls blob-field-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Abstract Symbol Generator</h2>
                    <ToolDescriptionPopover dialogLabel="Abstract Symbol Generator description">
                        <p>
                            Generates abstract script-like symbols from a binary grid.
                            Profiles are stylistic only and not linguistic text.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button type="button" className="generic-button" onClick={resetParameters}>
                        Reset parameters
                    </button>
                    <button type="button" className="generic-button" onClick={generate}>
                        Generate
                    </button>
                    <button type="button" className="generic-button" onClick={reseed}>
                        Reseed
                    </button>
                    <button type="button" className="generic-button" onClick={randomizeParameters}>
                        Randomize parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="symbol-generator"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <div className="tool-input-group">
                    <div className="field-row">
                        <CheckboxField
                            checked={liveGeneration}
                            onChange={setLiveGeneration}
                            label="Live generation"
                        />
                    </div>
                </div>

                <CollapsibleToolGroup title="Grid" icon={Grid3X3} defaultOpen={true}>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Rows"
                            info="Number of grid rows used to place cells. Smaller values create chunkier symbols."
                        >
                            <DebouncedInput type="number" min={4} max={40} step={1} value={rows} onChange={numberHandler(setRows, 4, 40)} />
                        </LabelWithInfo>
                    </div>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Columns"
                            info="Number of grid columns used to place cells. At low values (e.g. 6), symbols become blockier."
                        >
                            <DebouncedInput type="number" min={4} max={40} step={1} value={cols} onChange={numberHandler(setCols, 4, 40)} />
                        </LabelWithInfo>
                    </div>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Grid displacement strength (0-1)"
                            info="Controls seeded grid noise strength. 0 keeps equal cell sizing; 1 applies full displacement."
                        >
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={gridDisplacementStrength}
                                onChange={numberHandler(setGridDisplacementStrength, 0, 1)}
                            />
                        </LabelWithInfo>
                    </div>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Symmetry"
                            info="Mirrors active cells across vertical or horizontal axes after generation."
                        >
                            <select value={symmetry} onChange={(e) => setSymmetry(e.target.value as typeof symmetry)}>
                                <option value="none">None</option>
                                <option value="vertical">Vertical</option>
                                <option value="horizontal">Horizontal</option>
                            </select>
                        </LabelWithInfo>
                    </div>
                    <div className="field-row">
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                            <CheckboxField checked={showGridOverlay} onChange={setShowGridOverlay} label="Show grid overlay" inline />
                            <HoverInfoIcon text="Draws the underlying grid in preview to inspect cell positions." />
                        </div>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Paths" icon={Grid3X3} defaultOpen={true}>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Density (%)"
                            info="Target amount of active cells in the grid. Higher density fills more of the grid."
                        >
                            <DebouncedInput type="number" min={5} max={90} step={1} value={density} onChange={numberHandler(setDensity, 5, 90)} />
                        </LabelWithInfo>
                    </div>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Complexity (0-1)"
                            info="Controls how intricate generated structure becomes. Higher values add longer/more involved motifs."
                        >
                            <DebouncedInput type="number" min={0} max={1} step={0.05} value={complexity} onChange={numberHandler(setComplexity, 0, 1)} />
                        </LabelWithInfo>
                    </div>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Branching (0-1)"
                            info="How often paths fork into additional walkers. Higher values create more split branches."
                        >
                            <DebouncedInput type="number" min={0} max={1} step={0.05} value={branching} onChange={numberHandler(setBranching, 0, 1)} />
                        </LabelWithInfo>
                    </div>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Merge bias (0-1)"
                            info="Bias toward stepping near existing active cells. Higher values reconnect branches more often."
                        >
                            <DebouncedInput type="number" min={0} max={1} step={0.05} value={mergeBias} onChange={numberHandler(setMergeBias, 0, 1)} />
                        </LabelWithInfo>
                    </div>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Directional bias mode"
                            info="Preferred movement field for path growth: none, horizontal, vertical, radial, or swirl."
                        >
                            <select
                                value={directionalBiasMode}
                                onChange={(e) =>
                                    setDirectionalBiasMode(
                                        e.target.value as typeof directionalBiasMode,
                                    )
                                }
                            >
                                <option value="none">None</option>
                                <option value="horizontal">Horizontal</option>
                                <option value="vertical">Vertical</option>
                                <option value="radial">Radial</option>
                                <option value="swirl">Swirl</option>
                            </select>
                        </LabelWithInfo>
                    </div>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Directional bias strength (0-1)"
                            info="How strongly movement follows the selected directional field."
                        >
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={directionalBiasStrength}
                                onChange={numberHandler(
                                    setDirectionalBiasStrength,
                                    0,
                                    1,
                                )}
                            />
                        </LabelWithInfo>
                    </div>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Max path length (steps)"
                            info="Maximum contiguous stroke run before starting a new segment. Lower values create shorter chunks."
                        >
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={200}
                                step={1}
                                value={maxPathLength}
                                onChange={numberHandler(setMaxPathLength, 0, 200)}
                            />
                        </LabelWithInfo>
                    </div>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Path gap cells"
                            info="Number of skipped cells between one rendered path segment ending and the next starting."
                        >
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={4}
                                step={1}
                                value={pathGapCells}
                                onChange={numberHandler(setPathGapCells, 0, 4)}
                            />
                        </LabelWithInfo>
                    </div>
                    <div className="field-row">
                        <LabelWithInfo
                            label="Path output"
                            info="Single path keeps one continuous topology; multiple paths keeps disconnected components."
                        >
                            <select value={pathMode} onChange={(e) => setPathMode(e.target.value as typeof pathMode)}>
                                <option value="single">Single path</option>
                                <option value="multi">Multiple paths</option>
                            </select>
                        </LabelWithInfo>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Style" icon={Sparkles} defaultOpen={true}>
                    <div className="field-row">
                        <label className="field-label">
                            Style profile
                            <select value={styleProfile} onChange={(e) => setStyleProfile(e.target.value as typeof styleProfile)}>
                                <option value="neutral">Neutral</option>
                                <option value="bengali-like">Bengali-like</option>
                                <option value="cjk-like">CJK-like</option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke grammar
                            <select value={grammarMode} onChange={(e) => setGrammarMode(e.target.value as typeof grammarMode)}>
                                <option value="off">Off</option>
                                <option value="light">Light</option>
                                <option value="rich">Rich</option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Dot density (0-1)
                            <DebouncedInput type="number" min={0} max={1} step={0.05} value={dotDensity} onChange={numberHandler(setDotDensity, 0, 1)} />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Shape frequency (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={shapeFrequency}
                                onChange={numberHandler(setShapeFrequency, 0, 1)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Shape type
                            <select value={shapeType} onChange={(e) => setShapeType(e.target.value as typeof shapeType)}>
                                <option value="mixed">Mixed</option>
                                <option value="circle">Circle</option>
                                <option value="square">Square</option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Curvature (0-1)
                            <DebouncedInput type="number" min={0} max={1} step={0.05} value={curvature} onChange={numberHandler(setCurvature, 0, 1)} />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Corner style
                            <select value={cornerStyle} onChange={(e) => setCornerStyle(e.target.value as typeof cornerStyle)}>
                                <option value="sharp">Sharp</option>
                                <option value="rounded">Rounded</option>
                                <option value="bevel">Bevel</option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Corner amount (0-1)
                            <DebouncedInput type="number" min={0} max={1} step={0.05} value={cornerAmount} onChange={numberHandler(setCornerAmount, 0, 1)} />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke width
                            <DebouncedInput type="number" min={1} max={40} step={1} value={strokeWidth} onChange={numberHandler(setStrokeWidth, 1, 40)} />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke color
                            <DebouncedInput type="text" value={strokeColor} onChange={(v) => setStrokeColor(String(v))} />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Background color
                            <DebouncedInput type="text" value={backgroundColor} onChange={(v) => setBackgroundColor(String(v))} />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={exportBackground}
                            onChange={setExportBackground}
                            label="Export background"
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Seed and canvas" icon={Wand2} defaultOpen={true}>
                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <DebouncedInput type="text" value={seed} onChange={(v) => setSeed(String(v))} />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Width
                            <DebouncedInput type="number" min={200} max={1600} step={10} value={width} onChange={numberHandler(setWidth, 200, 1600)} />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Height
                            <DebouncedInput type="number" min={200} max={1600} step={10} value={height} onChange={numberHandler(setHeight, 200, 1600)} />
                        </label>
                    </div>
                </CollapsibleToolGroup>
            </div>

            {warning && <p className="upload-error">{warning}</p>}
        </div>
    );
}

function LabelWithInfo({
    label,
    info,
    children,
}: {
    label: string;
    info: string;
    children: React.ReactNode;
}) {
    return (
        <label className="field-label">
            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <span>{label}</span>
                <HoverInfoIcon text={info} />
            </span>
            {children}
        </label>
    );
}
